// Which set the timeline FocusPanel binds, given the current focus state.
// Pure + tiny on purpose: this rule has regressed repeatedly around the
// "every set logged" edge (the "weight/reps/RIR disappear when you tap back
// into a completed exercise" bug — reported ≥6 times, Leonardo 2026-06-28), so
// it lives outside the hook where it can be unit-tested in isolation.

export interface BoundSetArgs {
  /** Number of sets on the active entry; 0 when there's no active entry. */
  setCount: number;
  /** Athlete's explicit pill selection (SetCountRow), or null to follow the
   *  default. Reset to null on every focus switch. */
  selectedSetIdx: number | null;
  /** Index of the first uncompleted set, or -1 when every set is logged. */
  firstPendingIdx: number;
}

/**
 * Resolve the bound set index. Priority:
 *  1. an explicit, still-valid pill selection → edit ANY set;
 *  2. the first pending set → normal forward logging;
 *  3. every set logged → bind set 0 (set 1) so reps/weight/RIR stay visible
 *     when you re-focus a completed exercise (tap its timeline card). This is
 *     the SAME branch a past-session edit has always used (the "edit hides
 *     reps/rir" fix, 2026-05-31) — now it also covers a LIVE completed
 *     exercise. Returning -1 here was the long-standing bug (reported ≥6
 *     times, Leonardo 2026-06-28): the panel collapsed to the SetCountRow + ＋
 *     with no inputs, and only stepping a set with the arrows brought them
 *     back. Binding set 1 (Antenore's pick) keeps the superset round-robin
 *     untouched: it advances only when the user actively LOGS, never on focus.
 *  4. no sets at all → -1 (nothing to bind: the SetCountRow + ＋ prompt alone).
 */
export function resolveBoundSetIdx(args: BoundSetArgs): number {
  const { setCount, selectedSetIdx, firstPendingIdx } = args;
  if (setCount <= 0) return -1;
  if (
    selectedSetIdx !== null &&
    selectedSetIdx >= 0 &&
    selectedSetIdx < setCount
  ) {
    return selectedSetIdx;
  }
  if (firstPendingIdx >= 0) return firstPendingIdx;
  return 0;
}
